import cn from "classnames";
import { Transition } from "react-transition-group";
import { useRef, useEffect, MouseEvent, useState } from "react";
import { OverlayProps } from "./types";
import { preventBodyScroll } from "./utils";
import { DEFAULT_DURATION, TRANSITIONS } from "./constants";
import styles from "./styles.module.scss";

export const Overlay = ({
  children,
  className,
  transitionDuration = DEFAULT_DURATION,
  transitionName = TRANSITIONS.RISE_BOTTOM,
  fixed = false,
  opened = false,
  autofocus = false,
  shouldPreventBodyScroll = true,
  shouldCloseByEscapePress = false,
  shouldCloseByBackdropClick = false,
  mountOnEnter = true,
  unmountOnExit = true,
  dataTestId = "overlay",
  onClose,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  ...otherAttributes
}: OverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<Element>();

  const bringFocusInsideOverlay = () =>
    // Откладываем присваивание фокуса перед отрисовкой, чтобы предотвратить скачок прокрутки
    requestAnimationFrame(() => {
      if (overlayRef.current && document.activeElement && opened) {
        const isFocusOutsideOverlay = !overlayRef.current.contains(
          document.activeElement
        );

        if (isFocusOutsideOverlay) {
          // Определяем приоритетный элемент для назначения фокуса
          const autofocusElement = overlayRef.current.querySelector(
            "[autofocus]"
          ) as HTMLInputElement;

          if (autofocusElement) {
            autofocusElement.focus();
          } else {
            overlayRef.current.focus();
          }
        }
      }
    });

  const bringFocusBackToTarget = () =>
    // Откладываем присваивание фокуса перед отрисовкой, чтобы предотвратить скачок прокрутки
    requestAnimationFrame(() => {
      if (
        overlayRef.current &&
        document.activeElement &&
        previousActiveElement.current
      ) {
        // Возвращаем фокус на активный элемент
        const isFocusInsideOverlay = overlayRef.current.contains(
          document.activeElement
        );

        if (document.activeElement === document.body || isFocusInsideOverlay) {
          (previousActiveElement.current as HTMLElement).focus();
        }
      }
    });

  const handlePreventBodyScroll = (preventedScroll: boolean) => {
    if (shouldPreventBodyScroll) {
      preventBodyScroll(preventedScroll);
    }
  };

  const handleCloseByEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose?.(event);
    }
  };

  const [isMouseDown, setIsMouseDown] = useState(false);

  const handleBackdropMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (shouldCloseByBackdropClick && event.target === event.currentTarget) {
      setIsMouseDown(true);
    }
  };

  const handleBackdropMouseUp = (event: MouseEvent<HTMLDivElement>) => {
    setIsMouseDown(false);

    if (
      shouldCloseByBackdropClick &&
      event.target === event.currentTarget &&
      isMouseDown
    ) {
      onClose?.(event);
    }
  };

  const handleEnter = () => {
    handlePreventBodyScroll(true);
    onEnter?.();
  };

  const handleEntering = () => {
    onEntering?.();
  };

  const handleEntered = () => {
    if (autofocus && document.activeElement) {
      previousActiveElement.current = document.activeElement;
      bringFocusInsideOverlay();
    }

    onEntered?.();
  };

  const handleExit = () => {
    onExit?.();
  };

  const handleExiting = () => {
    bringFocusBackToTarget();
    onExiting?.();
  };

  const handleExited = () => {
    handlePreventBodyScroll(false);
    onExited?.();
  };

  const withTransition = transitionName !== null;

  useEffect(() => {
    if (shouldCloseByEscapePress) {
      document.addEventListener("keydown", handleCloseByEscape);

      return () => document.removeEventListener("keydown", handleCloseByEscape);
    }
  }, [shouldCloseByEscapePress]);

  useEffect(() => () => handlePreventBodyScroll(false), []);

  return (
    <Transition
      in={opened}
      timeout={withTransition ? transitionDuration : 0}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onEntered={handleEntered}
      onExit={handleExit}
      onExiting={handleExiting}
      onExited={handleExited}
      mountOnEnter={mountOnEnter}
      unmountOnExit={unmountOnExit}
    >
      {(state) => (
        <div
          ref={overlayRef}
          className={cn(className, styles.overlay, {
            fixed,
            [styles[`${transitionName}-${state}`]]: withTransition,
          })}
          onMouseDown={handleBackdropMouseDown}
          onMouseUp={handleBackdropMouseUp}
          data-test-id={dataTestId}
          {...otherAttributes}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};
